import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api.ts';
import { PRESENTATION, usePlatformLook } from '../platform.tsx';
import { ME_KEY, useMe } from '../auth.tsx';
import { Button, Field, Input, Loading } from '../components/ui.tsx';
import { LanguageSwitch } from '../shell/LanguageSwitch.tsx';
import { SISTEMA_NOME } from '../sistema.ts';
import { readFields } from './login-form.ts';

/** Entrada com e-mail e senha (ADR 0109). */
export function LoginPage() {
  const { t } = useTranslation();
  const { returnTo } = useSearch({ from: '/login' });
  const me = useMe();
  const queryClient = useQueryClient();
  // Faltou e-mail ou senha ao enviar.
  const [missing, setMissing] = useState(false);
  const { logoUrl } = usePlatformLook();
  const login = useMutation({
    mutationFn: (fields: { email: string; password: string }) =>
      api.post<{ mustChangePassword: boolean }>('/auth/login', fields),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ME_KEY }),
  });
  // Nunca volta para a própria tela de login depois de entrar.
  const target = returnTo && !returnTo.startsWith('/login') ? returnTo : '/';

  if (me.isPending)
    return (
      <div className="p-8">
        <Loading />
      </div>
    );
  if (me.data) return <Navigate to={target} />;

  // Os campos são lidos do formulário no envio: o navegador preenche e-mail e
  // senha guardados sem avisar a tela, e o botão não pode depender disso.
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fields = readFields(e.currentTarget, ['email', 'password'] as const);
    setMissing(!fields);
    if (fields) login.mutate(fields);
  };
  const error = missing
    ? t('auth.errors.missing')
    : login.error instanceof ApiError && login.error.code === 'invalid_credentials'
      ? t('auth.errors.invalid_credentials')
      : login.error
        ? t('auth.errors.unavailable')
        : null;

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-6 rounded-lg border border-rule bg-sheet p-8 shadow-sm">
        <div className="flex flex-col gap-1">
          <img
            src={logoUrl}
            alt={SISTEMA_NOME}
            className="mb-3 max-h-16 w-auto max-w-40 object-contain object-left"
          />
          <h1 className="text-xl font-semibold">{t('auth.title')}</h1>
          <p className="text-sm text-muted">{t('auth.subtitle')}</p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          {error && (
            <p
              role="alert"
              className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
            >
              {error}
            </p>
          )}
          <Field label={t('auth.email')}>
            <Input type="email" name="email" autoComplete="username" required autoFocus />
          </Field>
          <Field label={t('auth.password')}>
            <Input type="password" name="password" autoComplete="current-password" required />
          </Field>
          <Link
            to="/forgot-password"
            className="-mt-2 self-end text-sm text-brand-text underline-offset-2 hover:underline"
          >
            {t('auth.forgot')}
          </Link>
          <Button type="submit" className="py-2.5" disabled={login.isPending}>
            {login.isPending ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>
        <p className="text-xs text-muted">{t('auth.help')}</p>
        {PRESENTATION && (
          <Link to="/about" className="text-sm text-brand-text underline-offset-2 hover:underline">
            {t('presentation.backToSite')}
          </Link>
        )}
        <LanguageSwitch />
      </div>
    </div>
  );
}
